import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Divider from "antd/es/divider";
import { useIntl } from "react-intl";

import DefaultActivityTypeName from "@mapmycustomers/shared/enum/activity/DefaultActivityTypeName";
import { CustomFieldError } from "@mapmycustomers/shared/types/customField/CustomFieldValuesUpsertResponse";
import EmailAssociatedEntity from "@mapmycustomers/shared/types/email/EmailAssociatedEntity";
import { EntitiesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import {
  Button,
  DYNAMIC_VAR_REGEX,
  Modal,
  ModalsContext,
  Popconfirm,
  RecipientList,
  SubjectInput,
} from "@mapmycustomers/ui";

import { getEmailActivityType } from "@app/store/activity";
import { ActivityCreationPayloadWithCustomFields } from "@app/store/api/ApiService";
import { doesNeedToOpenActivityFormAfterSendingEmail, getEmailQuota } from "@app/store/email";
import { getCurrentUser } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useEmailBodyWithAddons from "@app/util/email/useEmailBodyWithAddons";
import { activityLayoutModel } from "@app/util/layout/impl";

import EmailRecipientTypeSelector from "../../EmailRecipientTypeSelector";
import AddEmailRecipientsModal from "../AddEmailRecipientsModal";
import CreateEmailActivitiesModal from "../CreateEmailActivitiesModal";
import CreateEmailBody from "../CreateEmailBody";
import CreateEmailContext from "../CreateEmailContext";
import EmailPreviewModal from "../EmailPreviewModal";
import EmailSendModal from "../EmailSendModal";
import EmailTemplateButton from "../EmailTemplateButton";

import styles from "./CreateEmailModal.module.scss";
import EmailActionButton from "./EmailActionButton";
import messages from "./messages";

type CreateEmailModalProps = {
  onHide: () => void;
} & ReturnType<typeof mapStateToProps>;

const CreateEmailModal: React.FC<CreateEmailModalProps> = ({
  currentUserId,
  emailActivityType,
  emailQuota,
  onHide,
  openActivityModal,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const [modalId, setModalId] = useState<string | undefined>();
  const { modals } = useNonNullableContext(ModalsContext);

  const modalStatus = useMemo(() => {
    return modalId ? modals.find((modal) => modal.id === modalId)?.status : undefined;
  }, [modalId, modals]);

  const {
    associatedEntities,
    attachedFiles,
    body,
    fetchAssociatedEntities,
    recipientMissedFields,
    recipients,
    recipientType,
    send,
    sending,
    setRecipients,
    setRecipientType,
    setSending,
    setSubject,
    setTemplateChanged,
    setUnsubscribeLinkIncluded,
    subject,
    template,
    unsubscribeLinkIncluded,
  } = useNonNullableContext(CreateEmailContext);

  const [createActivitiesModalOpen, showCreateActivitiesModal, hideCreateActivitiesModal] =
    useBoolean();
  const [addRecipientModalVisible, showAddRecipientModal, hideAddRecipientModal] = useBoolean();
  const [emailSendModalVisible, showEmailSendModal] = useBoolean();

  const emailBodyWithAddons = useEmailBodyWithAddons({ emailBody: body, unsubscribeLinkIncluded });

  const handleDeleteRecipient = useCallback(
    ({ id }: EntitiesSupportedByEmailFeature) => {
      setRecipients(recipients.filter((recipient) => recipient.id !== id));
    },
    [recipients, setRecipients]
  );

  const handleChangeSubject = useCallback(
    (value: string) => {
      setSubject(value);
      if (template) {
        setTemplateChanged(true);
      }
    },
    [template, setSubject, setTemplateChanged]
  );

  const [formLayout, setFormLayout] = useState(() => activityLayoutModel.defaultFormLayout);

  const bulkEmail = recipients.length > 1;

  const hasUsedDynamicVarVariants = useMemo(() => {
    return !!body.match(DYNAMIC_VAR_REGEX);
  }, [body]);

  const recipientTypeSelectorDisabledTooltip = useMemo(() => {
    if (template) {
      return intl.formatMessage(
        messages.recipientTypeSelectorDisabledBecauseOfAppliedTemplateTooltip
      );
    }
    if (hasUsedDynamicVarVariants) {
      return intl.formatMessage(
        messages.recipientTypeSelectorDisabledBecauseOfAppliedDynamicVariableTooltip
      );
    }
  }, [intl, template, hasUsedDynamicVarVariants]);

  useEffect(() => {
    if (unsubscribeLinkIncluded === undefined && recipients.length > 1) {
      setUnsubscribeLinkIncluded(true);
    }
  }, [unsubscribeLinkIncluded, setUnsubscribeLinkIncluded, recipients]);

  const handleCreate = useCallback(
    (
      emailAssociatedEntities: EmailAssociatedEntity[],
      crmActivity?: ActivityCreationPayloadWithCustomFields,
      customFieldsValidationCallback?: (customFieldErrors: CustomFieldError[]) => void
    ) => {
      if (recipientType && formLayout) {
        const childLayout = formLayout.childLayouts?.find(
          ({ crmActivityType }) => crmActivityType?.name === DefaultActivityTypeName.EMAIL
        );
        setSending(true);
        send({
          callback: () => {
            setSending(false);
            hideCreateActivitiesModal();
            showEmailSendModal();
            analytics.clicked(["Successfully Scheduled Emails"]);
          },
          email: {
            ...(attachedFiles.length
              ? {
                  attachments: attachedFiles
                    .filter((attachedFile) => attachedFile.file?.id)
                    .map((attachedFile) => attachedFile.file!.id),
                }
              : {}),
            associatedEntities: bulkEmail ? emailAssociatedEntities : [],
            body: emailBodyWithAddons,
            crmActivity: crmActivity ?? {
              name: subject,
              ...(emailActivityType
                ? { assigneeId: currentUserId, crmActivityType: { id: emailActivityType.id } }
                : {}),
            },
            entityIds: !bulkEmail ? [recipients[0].id] : undefined,
            entityType: recipientType,
            isHtml: true,
            layoutId: formLayout.id,
            ...(childLayout ? { childLayoutId: childLayout.id } : {}),
            logActivity: true,
            subject: subject,
            tracking: true,
          },
          failureCallback: (customFieldErrors) => {
            setSending(false);
            customFieldsValidationCallback?.(customFieldErrors);
          },
        });
      }
    },
    [
      analytics,
      attachedFiles,
      bulkEmail,
      currentUserId,
      emailActivityType,
      emailBodyWithAddons,
      subject,
      recipientType,
      formLayout,
      hideCreateActivitiesModal,
      send,
      recipients,
      setSending,
      showEmailSendModal,
    ]
  );

  const handleActivityModalCreate = useCallback(
    (
      crmActivity?: ActivityCreationPayloadWithCustomFields,
      customFieldsValidationCallback?: (customFieldErrors: CustomFieldError[]) => void
    ) => {
      handleCreate(associatedEntities, crmActivity, customFieldsValidationCallback);
    },
    [handleCreate, associatedEntities]
  );

  const handleSendClick = useCallback(() => {
    fetchAssociatedEntities({
      callback: (emailAssociatedEntities) => {
        if (openActivityModal) {
          showCreateActivitiesModal();
        } else {
          handleCreate(emailAssociatedEntities);
        }
      },
      recipients,
    });
  }, [
    fetchAssociatedEntities,
    openActivityModal,
    showCreateActivitiesModal,
    handleCreate,
    recipients,
  ]);

  const handleHide = useCallback(() => {
    analytics.clicked(["Closed"]);
    onHide();
  }, [analytics, onHide]);

  if (!recipientType || !formLayout) {
    return null;
  }

  return (
    <>
      <Modal
        className={styles.modal}
        closeIcon={
          <Popconfirm
            cancelText={intl.formatMessage(messages.closeConfirmationNo)}
            description={intl.formatMessage(messages.closeConfirmationTitle, {
              b: (text) => <b>{text}</b>,
            })}
            okText={intl.formatMessage(messages.closeConfirmationYes)}
            onConfirm={handleHide}
            overlayClassName={styles.confirmation}
          >
            <FontAwesomeIcon icon={faTimes} />
          </Popconfirm>
        }
        dynamic
        footer={null}
        maskClosable={false}
        onIdChange={setModalId}
        open={!emailSendModalVisible}
        scrollable
        subtitle={intl.formatMessage(messages.subTitle, {
          sent: emailQuota?.remaining ?? 0,
          total: emailQuota?.total ?? 0,
        })}
        title={
          <>
            <span>{intl.formatMessage(messages.title)}</span>
            {modalStatus !== "minimized" && (
              <EmailRecipientTypeSelector
                disabled={!!recipients.length || !!recipientTypeSelectorDisabledTooltip}
                disabledTooltip={recipientTypeSelectorDisabledTooltip}
                onChange={setRecipientType}
                value={recipientType}
              />
            )}
          </>
        }
        width="40rem"
      >
        <div className={styles.container}>
          <EmailTemplateButton />
          <RecipientList
            action={
              <Button
                onClick={showAddRecipientModal}
                onMouseDown={(e) => e.preventDefault()}
                size="small"
                type="link"
              >
                {intl.formatMessage(messages.addRecipientButtonLabel)}
              </Button>
            }
            entities={recipients}
            label={intl.formatMessage(messages.to)}
            missingFields={recipientMissedFields}
            moreText={intl.formatMessage(messages.more)}
            onDeleteEntity={handleDeleteRecipient}
          />
          <Divider className={styles.divider} />
          <SubjectInput onChange={handleChangeSubject} value={subject} />
          <Divider className={styles.divider} />
          <CreateEmailBody
            action={
              <EmailActionButton
                emailSending={sending}
                onClick={handleSendClick}
                openActivityModal={openActivityModal}
              />
            }
          />
        </div>
      </Modal>
      <CreateEmailActivitiesModal
        formLayout={formLayout}
        onBack={hideCreateActivitiesModal}
        onChangeFormLayout={setFormLayout}
        onCreate={handleActivityModalCreate}
        onHide={onHide}
        open={createActivitiesModalOpen}
        subject={subject}
      />
      <EmailSendModal onHide={onHide} open={emailSendModalVisible} />
      <EmailPreviewModal />
      <AddEmailRecipientsModal onHide={hideAddRecipientModal} visible={addRecipientModalVisible} />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUser(state)!.id,
  emailActivityType: getEmailActivityType(state),
  emailQuota: getEmailQuota(state),
  openActivityModal: doesNeedToOpenActivityFormAfterSendingEmail(state),
});

export default connect(mapStateToProps)(CreateEmailModal);
